import React from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateBody,
  Grid,
  GridItem,
  Label,
  LabelGroup,
} from '@patternfly/react-core';
import { InfoCircleIcon } from '@patternfly/react-icons';
import type { CertificateX509Details, CertificateX509Name } from '../../../types/models/Certificate';
import { copyable, text } from './displayHelpers';

interface CertificateX509TabProps {
  x509: CertificateX509Details | null;
}

const KEY_USAGE_LABELS: Record<string, string> = {
  digitalSignature: 'Digital signature',
  nonRepudiation: 'Non-repudiation',
  contentCommitment: 'Content commitment',
  keyEncipherment: 'Key encipherment',
  dataEncipherment: 'Data encipherment',
  keyAgreement: 'Key agreement',
  keyCertSign: 'Certificate signing',
  cRLSign: 'CRL signing',
  encipherOnly: 'Encipher only',
  decipherOnly: 'Decipher only',
};

// phpseclib OID names, humanized.
const EXTENDED_KEY_USAGE_LABELS: Record<string, string> = {
  'id-kp-serverAuth': 'Server authentication',
  'id-kp-clientAuth': 'Client authentication',
  'id-kp-codeSigning': 'Code signing',
  'id-kp-emailProtection': 'Email protection',
  'id-kp-timeStamping': 'Time stamping',
  'id-kp-OCSPSigning': 'OCSP signing',
};

function usageChips(values: string[], labels: Record<string, string>, groupLabel: string): React.ReactNode {
  if (values.length === 0) {
    return '—';
  }
  return (
    <LabelGroup aria-label={groupLabel} numLabels={10}>
      {values.map((value) => (
        <Label key={value} isCompact variant="outline">
          {labels[value] ?? value}
        </Label>
      ))}
    </LabelGroup>
  );
}

function yesNoLabel(value: boolean, yesColor: 'blue' | 'orange'): React.ReactNode {
  return (
    <Label color={value ? yesColor : 'grey'} isCompact>
      {value ? 'Yes' : 'No'}
    </Label>
  );
}

function dnCard(title: string, dn: string | null, name: CertificateX509Name): React.ReactNode {
  return (
    <Card isFullHeight>
      <CardTitle>{title}</CardTitle>
      <CardBody>
        <DescriptionList isHorizontal isCompact>
          <DescriptionListGroup>
            <DescriptionListTerm>Distinguished name</DescriptionListTerm>
            <DescriptionListDescription>
              {copyable(dn, `${title.toLowerCase()} distinguished name`)}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Common name</DescriptionListTerm>
            <DescriptionListDescription>{text(name.common_name)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Organization</DescriptionListTerm>
            <DescriptionListDescription>{text(name.organization)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Organizational unit</DescriptionListTerm>
            <DescriptionListDescription>{text(name.organizational_unit)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Locality</DescriptionListTerm>
            <DescriptionListDescription>{text(name.locality)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>State / Province</DescriptionListTerm>
            <DescriptionListDescription>{text(name.state)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Country</DescriptionListTerm>
            <DescriptionListDescription>{text(name.country)}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Email</DescriptionListTerm>
            <DescriptionListDescription>{text(name.email)}</DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>
      </CardBody>
    </Card>
  );
}

function CertificateX509Tab({ x509 }: CertificateX509TabProps) {
  if (x509 === null) {
    return (
      <EmptyState
        titleText="Extended details unavailable"
        icon={InfoCircleIcon}
        variant="lg"
        headingLevel="h2"
        style={{ marginTop: '1rem' }}
      >
        <EmptyStateBody>
          No certificate body is stored for this entry yet (for example while a request is still being issued), or the
          stored PEM could not be parsed. The row-level information remains available on the Overview tab.
        </EmptyStateBody>
      </EmptyState>
    );
  }

  const publicKey = x509.public_key;
  const keyType =
    publicKey.type !== null
      ? `${publicKey.type}${publicKey.curve !== null ? ` (${publicKey.curve})` : ''}`
      : '—';

  return (
    <Grid hasGutter md={6} style={{ marginTop: '1rem' }}>
      <GridItem>{dnCard('Subject', x509.subject_dn, x509.subject)}</GridItem>
      <GridItem>{dnCard('Issuer', x509.issuer_dn, x509.issuer)}</GridItem>

      <GridItem>
        <Card isFullHeight>
          <CardTitle>Public key &amp; signature</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Key type</DescriptionListTerm>
                <DescriptionListDescription>{keyType}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Key size</DescriptionListTerm>
                <DescriptionListDescription>
                  {publicKey.bits !== null ? `${publicKey.bits} bits` : '—'}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Signature algorithm</DescriptionListTerm>
                <DescriptionListDescription>{text(x509.signature_algorithm)}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>X.509 version</DescriptionListTerm>
                <DescriptionListDescription>
                  {x509.version !== null ? `v${x509.version}` : '—'}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Serial (hex)</DescriptionListTerm>
                <DescriptionListDescription>{copyable(x509.serial_hex, 'serial number')}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>SHA-1 fingerprint</DescriptionListTerm>
                <DescriptionListDescription>
                  {copyable(x509.fingerprint_sha1, 'SHA-1 fingerprint')}
                </DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>

      <GridItem>
        <Card isFullHeight>
          <CardTitle>Usage &amp; properties</CardTitle>
          <CardBody>
            <DescriptionList isHorizontal isCompact>
              <DescriptionListGroup>
                <DescriptionListTerm>Key usage</DescriptionListTerm>
                <DescriptionListDescription>
                  {usageChips(x509.key_usage, KEY_USAGE_LABELS, 'Key usages')}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Extended key usage</DescriptionListTerm>
                <DescriptionListDescription>
                  {usageChips(x509.extended_key_usage, EXTENDED_KEY_USAGE_LABELS, 'Extended key usages')}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>CA certificate</DescriptionListTerm>
                <DescriptionListDescription>{yesNoLabel(x509.is_ca, 'blue')}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Self-signed</DescriptionListTerm>
                <DescriptionListDescription>{yesNoLabel(x509.is_self_signed, 'orange')}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>
      </GridItem>
    </Grid>
  );
}

export default CertificateX509Tab;
